import { Feature } from "ol";
import { buffer } from "ol/extent";
import OL3Parser from "jsts/org/locationtech/jts/io/OL3Parser";
import { BufferOp } from "jsts/org/locationtech/jts/operation/buffer";
import { Point, LineString, Polygon, MultiPoint, MultiLineString, MultiPolygon } from "ol/geom";
import LinearRing from "ol/geom/LinearRing";
import { getMap } from "../Forms/DrawingForm";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Fill, Stroke, Style } from "ol/style";
import { EsriJSON } from "ol/format";

export interface IEsriGeometry {
    rings: number[][][];
}

const esrijsonFormat = new EsriJSON();

const parser = new OL3Parser();
parser.inject(Point, LineString, LinearRing, Polygon, MultiPoint, MultiLineString, MultiPolygon);

export const getBuffer = (feature: Feature, distance?: number) =>
    buffer(feature.getGeometry().getExtent(), distance || 100);

export const cloneFeatures = (features: Feature[]): Feature[] => features.map((f) => f.clone());

export const getMultiPolygonGeometry = (features: Feature[]): IEsriGeometry => {
    const bufferFeatures = bufferizeFeatures(cloneFeatures(features), 5);
    // if you want to see buffers, use this function
    // visualizeBuffers(bufferFeatures);

    const geometries = bufferFeatures.map((f) => {
        const esriGeometry = esrijsonFormat.writeGeometry(f.getGeometry());
        return JSON.parse(esriGeometry).rings[0];
    });
    const rings = geometries.filter((geometry) => geometry.length); // removes empty rings, to prevent geometryService error
    const geom = { hasZ: false, hasM: false, rings };
    return geom;
};

export const bufferizeFeatures = (features: Feature[], distance?: number): Feature[] => {
    for (let i = 0; i < features.length; i += 1) {
        const jstsGeometry = parser.read(features[i].getGeometry());
        const bo = new BufferOp(jstsGeometry);
        const buffered = bo.getResultGeometry(distance || 10);
        const geometry = parser.write(buffered);

        features[i].setGeometry(geometry.simplify(4));
    }

    return features;
};

const createVectorLayer = (source: VectorSource) =>
    new VectorLayer({
        source,
        style: () => {
            return new Style({
                fill: new Fill({ color: [0, 0, 255, 0.5] }),
                stroke: new Stroke({ color: "blue", width: 3 })
            });
        },
        zIndex: 1000
    });

const visualizeBuffers = (features: Feature[]) => getMap().addLayer(createVectorLayer(new VectorSource({ features })));

export const isWorkingDay = (date: Date): boolean => {
    const comparableDay = date.getDate() + "." + (date.getMonth() + 1);
    const isSaturday = date.getDay() === 6;
    const isSunday = date.getDay() === 0;
    const holidays = getHolidays(date.getFullYear());
    const isHoliday = holidays.includes(comparableDay);

    return !isHoliday && !isSaturday && !isSunday;
};

export const getEasterDate = (year: number): Date => {
    const date = new Date();
    // Set the timestamp to midnight.
    date.setHours(0, 0, 0, 0);
    // Set the year.
    date.setFullYear(year);
    // Find the golden number.
    const a = year % 19;
    // Choose which version of the algorithm to use based on the given year.
    const b = 2200 <= year && year <= 2299 ? (11 * a + 4) % 30 : (11 * a + 5) % 30;
    // Determine whether or not to compensate for the previous step.
    const c = b === 0 || (b === 1 && a > 10) ? b + 1 : b;
    // Use c first to find the month: April or March.
    const m = 1 <= c && c <= 19 ? 3 : 2;
    // Then use c to find the full moon after the northward equinox.
    const d = (50 - c) % 31;
    // Mark the date of that full moon—the "Paschal" full moon.
    date.setMonth(m, d);
    // Count forward the number of days until the following Sunday (Easter).
    date.setMonth(m, d + (7 - date.getDay()));

    // Gregorian Western Easter Sunday
    return date;
};

export const addDays = (date: Date, days: number): Date =>
    new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + days,
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );

const getHolidays = (year: number) => {
    const LVHolidays = ["1.1", "1.5", "4.5", "23.6", "24.6", "18.11", "24.12", "25.12", "31.12"];

    const easter = getEasterDate(year);
    const bigFriday = addDays(easter, -2);
    const secondEaster = addDays(easter, 1);

    LVHolidays.push(
        bigFriday.getDate() + "." + (bigFriday.getMonth() + 1),
        secondEaster.getDate() + "." + (secondEaster.getMonth() + 1)
    );

    return LVHolidays;
};

export const getFirstAllowedWorkday = (workDayGap: number): Date => {
    const today = new Date(Date.now());
    today.setHours(0);
    today.setMinutes(0);
    today.setSeconds(0);
    today.setMilliseconds(0);
    let minAllowedWorkDay: Date;
    let workDays = 0;

    for (let i = 1; i < 30; i++) {
        const incrementedDay = addDays(today, i);
        !minAllowedWorkDay && isWorkingDay(new Date(incrementedDay)) && workDays++;

        if (workDays >= workDayGap) {
            minAllowedWorkDay = incrementedDay;
            break;
        }
    }

    return minAllowedWorkDay;
};

export const formatDate = (date: string): string => date && date.split("-").reverse().join(".");
